// src/App.tsx
import { Suspense, lazy, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { validateToken, getCurrentUser } from "./store/slices/authSlice";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { useInitializeApp } from "./hooks/useInitializeApp";
import { LoadingSpinner } from "./components/common/LoadingSpinner";

// Lazy load page components
const HomePage = lazy(() => import("./pages/HomePage"));
const DoctorsPage = lazy(() => import("./pages/doctors"));
const DoctorPage = lazy(() => import("./pages/doctors/slug"));
const ManagedServicesPage = lazy(() => import("./pages/managedservices"));
const ManagedServiceDetailsPage = lazy(
  () => import("./pages/managedservices/slug")
);
const BookAppointment = lazy(() => import("./pages/book-appointment"));
const ArticlesPage = lazy(() => import("./pages/articles"));
const ArticlePage = lazy(() => import("./pages/articles/slug"));
const Contact = lazy(() => import("./pages/contact"));
const AdminPage = lazy(() => import("./pages/admin"));
const SignInPage = lazy(() => import("./pages/auth/signin"));

// Custom loading component for better UX
const PageLoadingFallback = () => (
  <div className="flex h-screen items-center justify-center">
    <LoadingSpinner />
  </div>
);

function App() {
  //console.log("App.tsx started");
  const { isInitialized } = useInitializeApp();
  //console.log("Initialization complete");
  const dispatch = useAppDispatch();
  /*const { isAuthenticated, user, token, loading } = useAppSelector(
    (state) => state.auth
  );*/
  const { user, token, loading } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (token && !user && !loading) {
      // Token exists and no user data, so validate token and fetch user details
      dispatch(validateToken())
        .unwrap()
        .then(() => {
          // After token validation, fetch the current user
          dispatch(getCurrentUser());
        })
        .catch((error) => {
          console.error("Token validation failed:", error);
        });
    }
  }, [dispatch, token, user, loading]);

  if (!isInitialized) {
    return <PageLoadingFallback />;
  }

  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/doctors" element={<DoctorsPage />} />
        <Route path="/doctors/:slug" element={<DoctorPage />} />
        <Route path="/managedservices" element={<ManagedServicesPage />} />
        <Route
          path="/managedservices/:slug"
          element={<ManagedServiceDetailsPage />}
        />
        <Route path="/book-appointment" element={<BookAppointment />} />
        <Route path="/articles" element={<ArticlesPage />} />
        <Route path="articles/:slug" element={<ArticlePage />} />
        <Route path="/contact" element={<Contact />} />

        {/* Auth Routes */}
        <Route path="/auth/signin" element={<SignInPage />} />

        {/* Protected Admin Routes */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminPage />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}

export default App;
